import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { Col } from '@/components/Col';
import type { FormProviderProps } from '@/components/FormProvider';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import type { ModalProps } from '@/components/Modal';
import { Modal } from '@/components/Modal';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import { serviceMessages } from '@/messages/service';

import { forgotRepairNumberSchema } from './schema';

import * as S from './ForgottenRepairNumberModal.styles';

export type ForgottenRepairNumberModalProps = ModalProps &
  Pick<FormProviderProps, 'onSubmit'> & {
    isProcessing?: boolean;
    onHeaderClose?: () => void;
  };
export const ForgottenRepairNumberModal = ({
  isProcessing,
  onHeaderClose,
  onSubmit,
  ...props
}: ForgottenRepairNumberModalProps) => {
  const { formatMessage } = useIntl();
  const schema = forgotRepairNumberSchema(formatMessage);
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });
  const { clearErrors, reset } = formCtx;
  const formReset = () => {
    reset();
    clearErrors();
  };
  useEffect(() => {
    formReset();
  }, [props.isOpen]);
  return (
    <Modal {...props}>
      <Modal.Header divider onClose={onHeaderClose}>
        <SectionHeader
          numberOfColoredWords={2}
          sizeDesktop={40}
          sizeMobile={34}
          text={formatMessage(serviceMessages.forgotRepairNumber)}
          textAlign='center'
        />
      </Modal.Header>
      <Modal.Body>
        <FormProvider
          name='forgotRepairNumber'
          {...formCtx}
          onSubmit={onSubmit}
        >
          <S.Empty
            alignItems='center'
            flexDirection='column'
            gridGap={2}
            maxWidth='537px'
            py={1}
          >
            <Typography textAlign='center'>
              {formatMessage(serviceMessages.forgotRepairNumberTip)}
            </Typography>
            <Box maxWidth={400} width='100%'>
              <InputField
                autoComplete='email'
                colorVariant='grey'
                data-testid='inputForgotEmail'
                name='email'
                placeholder={formatMessage(serviceMessages.email)}
                required
              />
            </Box>
            <S.Wrapper gapDensity='none' mt={2}>
              <Col sm={4} smStart={5} xs={12}>
                <Button
                  data-testid='buttonForgotSubmit'
                  disabled={isProcessing}
                  isFullWidth
                  isProcessing={isProcessing}
                  type='submit'
                >
                  {formatMessage(serviceMessages.send)}
                </Button>
              </Col>
            </S.Wrapper>
          </S.Empty>
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
};
